/**
 * `AuthService` — `/api/v1/auth/*`.
 *
 * Модуль тонкий и без состояния: путь, типы запроса и ответа, разворот
 * `response.data`. Ни ретраев, ни кэша, ни обработки ошибок — первое живёт в
 * `lib/http.ts`, второе и третье в сторе.
 *
 * Ошибки наружу выходят уже нормализованными в `ApiError` (response-интерцептор),
 * поэтому `try/catch` здесь был бы пустым слоем.
 *
 * Из всего `AuthService` приложению стойки нужны четыре метода: вход, ротация,
 * выход и «кто я». Смены пароля, списка сессий и отзыва устройств здесь нет
 * намеренно — их место в админке, а метод, которого нет в модуле, нельзя
 * случайно позвать с экрана сканирования.
 */

import { AUTH_REFRESH_PATH, http } from '@/lib/http'
import type {
  GetMeResponse,
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshRequest,
  RefreshResponse,
} from '@/types/api'

export const authApi = {
  /**
   * `POST /api/v1/auth/login` — публичный.
   * `login` принимает email **или** username одним полем, регистр не важен.
   *
   * `skipAuth`: старого токена может не быть вовсе, а если он есть и протух —
   * 401 от логина обязан означать `INVALID_CREDENTIALS`, а не запускать ротацию.
   */
  async login(body: LoginRequest): Promise<LoginResponse> {
    const { data } = await http.post<LoginResponse>('/api/v1/auth/login', body, { skipAuth: true })
    return data
  },

  /**
   * `POST /api/v1/auth/refresh` — публичный.
   *
   * ⚠️ Приложению этот метод почти никогда не нужен: ротацией занимается
   * `refreshOnce()` из `lib/http.ts`, и звать надо **её** — только она
   * single-flight, а сервер ротирует токен на каждое использование.
   * Обёртка существует ради полноты контрактной поверхности и ручной отладки.
   */
  async refresh(body: RefreshRequest): Promise<RefreshResponse> {
    const { data } = await http.post<RefreshResponse>(AUTH_REFRESH_PATH, body, { skipAuth: true })
    return data
  },

  /**
   * `POST /api/v1/auth/logout` — публичный и идемпотентный.
   *
   * `skipAuth` здесь не «потому что можно», а потому что нужно: выход обязан
   * срабатывать и с протухшим access-токеном. Без флага 401 запустил бы
   * ротацию ради того, чтобы тут же погасить сессию, — а при мёртвом refresh
   * выход бы вовсе не состоялся.
   *
   * Ответ (`LogoutResponse`) пуст по контракту, поэтому наружу не отдаётся.
   */
  async logout(body: LogoutRequest): Promise<void> {
    await http.post('/api/v1/auth/logout', body, { skipAuth: true })
  },

  /**
   * `GET /api/v1/auth/me` — любая роль.
   *
   * Отдаёт `user` и плоский список `permissions`. Права стойке не нужны:
   * `ResolveParticipantQr` разрешён `anyRole`, ролевого гейта в приложении нет
   * и авторитетом остаётся сервер. Берём отсюда только пользователя.
   */
  async me(): Promise<GetMeResponse> {
    const { data } = await http.get<GetMeResponse>('/api/v1/auth/me')
    return data
  },
}
